import 'ol/ol.css';
import OlMap from 'ol/Map';
import View from 'ol/View';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { renderCages } from './cage';
import { renderBuoys } from './buoy';
import { MooringDrawingSetup } from 'models/mooring';
import TileLayer from 'ol/layer/Tile';
import { OSM } from 'ol/source';
import { calculateCornersFromCenter, convertToDecimalDegrees, getCornerPoints } from './helpers';
import { fromLonLat } from 'ol/proj';
import { renderFleet } from './fleet';
import { MapFeatureType, MapRenderOptions, OnFeatureClick, OnAddServiceCorridor } from './renderOptions';
import { createBuoyGrid } from './buoy/transform';
import { renderGridLines } from './gridLine';
import { renderAnchorLines } from './anchorLine';
import Feature from 'ol/Feature';
import { Coordinate } from 'ol/coordinate';
import { MapBrowserEvent } from 'ol';
import { calculateServiceCorridorData } from './gridLine/serviceCorridor';
import { ensureNumberOrDefault } from 'lib/helpers';

const maps = new Map<string, OlMap>();

export type MapArgs = {
  renderOptions: MapRenderOptions;
  onFeatureClick?: OnFeatureClick;
  onAddServiceCorridor?: OnAddServiceCorridor;
  containerId?: string;
  zoom?: number;
  resetMap?: boolean;
};

type MapManagerArgs = {
  center: Coordinate;
};

export interface Map {
  resetCenter(center?: Coordinate): void;
}

export class MapManager implements Map {
  private map: OlMap;
  private args: MapManagerArgs;

  constructor(map: OlMap, args?: MapManagerArgs) {
    this.map = map;
    this.args = args ?? { center: [0, 0] };
  }

  /**
   * Reset the map center to the initial center or a new center if provided.
   * @param center Optional new center coordinate to set.
   */
  public resetCenter(center?: Coordinate) {
    this.map.getView().setCenter(fromLonLat(center ?? this.args.center));
  }
}

let clickHandler: (event: MapBrowserEvent) => void;

const defaultCenter = [6.145437803152079, 62.46489778476671];

/**
 * Render the mooring map with the provided data and options.
 * @param data The mooring drawing setup data.
 * @param renderOptions Options for rendering the map features.
 * @param onFeatureClick Optional callback for feature click events.
 * @param onAddServiceCorridor Optional callback for adding service corridor.
 * @returns An instance of MapManager to manage the rendered map.
 */
export const renderMap = (
  data: MooringDrawingSetup,
  { renderOptions, onFeatureClick, onAddServiceCorridor, containerId = 'map', zoom = 17, resetMap = false }: MapArgs
) => {
  let center = [...defaultCenter];
  if (data.CenterPositionLong && data.CenterPositionLat) {
    const centerLong = convertToDecimalDegrees(data.CenterPositionLong || data.NWPositionLong);
    const centerLat = convertToDecimalDegrees(data.CenterPositionLat || data.NWPositionLat);
    center = [centerLong, centerLat];
  }

  let map = maps.get(containerId);

  if (map) {
    const layers = map.getLayers().getArray();
    for (const layer of layers) {
      if (layer.getClassName() !== 'mooring') continue;
      map.removeLayer(layer);
    }

    const target = document.getElementById(containerId);
    map.setTarget(target);
    if (resetMap) {
      map.getView().setCenter(fromLonLat(center));
      map.getView().setZoom(zoom);
    }
  } else {
    map = new OlMap({
      target: containerId,
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
      ],
      view: new View({
        center: fromLonLat(center),
        zoom: zoom,
      }),
    });
    maps.set(containerId, map);
  }

  const vectorSource = new VectorSource();
  const vectorLayer = new VectorLayer({
    source: vectorSource,
    className: 'mooring',
  });
  map.addLayer(vectorLayer);

  const rows = data.Buoys.map((x) => x.Row)
    .sort()
    .at(-1);

  const columns = data.Buoys.map((x) => x.Column)
    .sort()
    .at(-1);

  const corners = getCornerPoints(data, center, columns - 1, rows - 1);

  const grid = createBuoyGrid(corners, columns, rows, data.Buoys, data.GridLineLength, data.ServiceCorridor, vectorSource);

  renderBuoys({ grid, vectorSource, buoys: data.Buoys }, renderOptions);
  renderCages({ grid, vectorSource, cages: data.Cages }, renderOptions);
  renderGridLines({ grid, vectorSource, gridLines: data.GridLines }, renderOptions);
  renderAnchorLines({ grid, vectorSource, anchorLines: data.AnchorLines }, renderOptions);

  if (data.FleetCenterPositionLat && data.FleetCenterPositionLong) {
    const fleetCenter = [convertToDecimalDegrees(data.FleetCenterPositionLong), convertToDecimalDegrees(data.FleetCenterPositionLat)];
    renderFleet(
      {
        vectorSource,
        fleetAnchorLines: data.FleetAnchorLines,
        fleetCenter,
        rotation: 90 + data.FleetAngleFromNorth,
      },
      renderOptions
    );
  }

  // map.getView().fit(vectorSource.getExtent(), {
  //   size: map.getSize(),
  //   padding: [50, 50, 50, 50],
  //   maxZoom: 18,
  // });

  // Add hover and click interactivity
  // let lastHoveredFeature: Feature[] | null =a[b];

  // map.on('pointermove', (event) => {
  //   // Reset the style of the previously hovered feature

  //   // Get the feature under the mouse
  //   map.forEachFeatureAtPixel(event.pixel, (feature) => {
  //     if (!(feature instanceof Feature)) return;

  //     if (feature.get('type') === 'cage') {
  //       lastHoveredFeature = feature; // Store the currently hovered feature
  //       feature.setStyle(cageStyle('123', { hover: true })); // Apply hover style
  //     }

  //     return true; // Stop iteration after finding the first feature
  //   });
  // });

  if (clickHandler) {
    map.un('click', clickHandler);
  }

  clickHandler = (event: MapBrowserEvent) => {
    map.forEachFeatureAtPixel(event.pixel, (feature) => {
      if (!(feature instanceof Feature)) return;

      const type = feature.get('type') as MapFeatureType;
      const id = feature.get('id') as number;

      if (type && id) {
        // Handle service corridor tool
        if (renderOptions.activeTool === 'add-service-corridor' && type === 'gridLine') {
          const corridorData = calculateServiceCorridorData(id, grid, data.GridLines);
          if (corridorData && onAddServiceCorridor) {
            onAddServiceCorridor(corridorData);
          }

          renderMap(data, {
            renderOptions,
            onFeatureClick,
            onAddServiceCorridor,
            containerId,
            zoom,
            resetMap: true,
          });
          return true;
        }

        // Normal click handling
        onFeatureClick?.({
          type,
          id,
        });
      }
      return true; // Stop iteration after finding the first feature
    });
  };

  map.on('click', clickHandler);

  return new MapManager(map, { center });
};
